import React from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'
import Typography from '@mui/material/Typography'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSparkles } from '@fortawesome/pro-regular-svg-icons'
import { LabraChatProvider } from '@labrav/common'
import { FundingToDos, FundingTodo } from '@labrav/funding'
import { useSalesforceContext } from '../contexts/SalesforceContext'
import { SfActionType } from '../types/salesforce'
import { useCanvasContentResize } from '../hooks/useCanvasContentResize'
import { FundingOutletContext } from './FundingLayout'
import { chatServiceUrl } from '../../../common/utils/constants'
import { useStyles } from './FundingTodosWrapper.styles'

/**
 * Salesforce Canvas tile for the funding ToDos + send-box. Reuses the funding
 * MFE's `<FundingToDos>` but overrides its action via `onSendPrompt`: instead of
 * opening the in-panel chat on the funding dashboard, it navigates within the
 * same Canvas iframe to the `/funding/chat` route and lets `useCanvasContentResize`
 * on that page grow the host iframe to fit the chat UI.
 *
 * An "Ask Labra AI" button sits on the card heading row and opens the same chat
 * route with no seeded prompt (a general entry point). It is passed as
 * `headerAction` so the funding dashboard layout stays untouched.
 *
 * `<LabraChatProvider>` is only here to satisfy `<FundingToDos>`'s internal
 * `useLabraChat()` hook (it throws without a provider); the actual send is
 * diverted through `onSendPrompt`, so the provided ids are never used to chat.
 *
 * The root element is measured via `useCanvasContentResize` so the native-package
 * LWC iframe hugs this content instead of leaving empty space in a fixed frame.
 */
export const FundingTodosWrapper: React.FC = () => {
  const { classes } = useStyles()
  const { partnerId, actions } = useSalesforceContext()
  const navigate = useNavigate()
  const rootRef = useCanvasContentResize<HTMLDivElement>()

  const { todos, loading } = useOutletContext<FundingOutletContext>()

  // `text` seeds the chat's opening message; omit it for a plain "open the
  // agent" entry point (the Ask Labra AI button).
  const openChatInIframe = (text?: string) => {
    if (!partnerId) return
    const search = text
      ? `?${new URLSearchParams({ prompt: text }).toString()}`
      : ''
    navigate({ pathname: '../chat', search }, { relative: 'path' })
  }

  const handleTodoClick = (todo: FundingTodo): boolean => {
    if (todo.action !== SfActionType.OpenSfCosell) return false
    actions.triggerAction(todo.action)
    return true
  }

  const askButton = (
    <button
      type="button"
      className={classes.askBtn}
      onClick={() => openChatInIframe()}
      data-testid="funding-ask-agent"
    >
      <FontAwesomeIcon icon={faSparkles} className={classes.askIcon} />
      <Typography component="span" className={classes.askLabel}>
        Ask Labra AI
      </Typography>
    </button>
  )

  return (
    <LabraChatProvider
      partnerId={partnerId}
      accountId={partnerId}
      userId=""
      chatEndpoint={chatServiceUrl}
      agentType="supervisor_agent"
    >
      <div ref={rootRef} className={classes.root}>
        <FundingToDos
          todos={todos}
          loading={loading}
          title="ToDos & AI recommendations"
          headerAction={askButton}
          onSendPrompt={openChatInIframe}
          onTodoClick={handleTodoClick}
        />
      </div>
    </LabraChatProvider>
  )
}

export default FundingTodosWrapper
